import { Container, VDBText } from "@components/atoms";
import { Pressable, ScrollView } from "react-native";
import styled from "styled-components/native";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { CollectionMenuItem } from "@src/types/models";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/native";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { useSelector } from "react-redux";
import { ExportStatus } from "@src/types/ExportsResult";

type BulkActionsContentProps = {
  itemCount: number;
  selectOption: (value: string) => void;
  menuOptions: Array<CollectionMenuItem>;
  noPadding?: boolean;
  hideArrowIcon?: boolean;
  totalItems?: number;
};

const BulkActionsContent = ({
  itemCount,
  totalItems,
  selectOption,
  menuOptions,
  noPadding,
  hideArrowIcon,
}: BulkActionsContentProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const latestExportData = useSelector(
    (state: any) => state.appState.latestExportData
  );
  const appSettings = useAppSettings();
  const exportEnabled = appSettings?.exports_enabled;
  const { deviceWidth: screenWidth } = useIsFoldable();
  const bulkItemCount = Number(appSettings?.bulk_item_count) || 50;
  const rawBulkOrderLimit = appSettings?.bulk_order_limit_count;
  const parsedBulkOrderLimit = Number(rawBulkOrderLimit);
  const hasBulkOrderLimit =
    rawBulkOrderLimit !== undefined &&
    rawBulkOrderLimit !== null &&
    String(rawBulkOrderLimit).trim() !== "" &&
    Number.isFinite(parsedBulkOrderLimit);
  const bulkOrderLimitCount = hasBulkOrderLimit ? parsedBulkOrderLimit : null;
  const validOptions = [
    SearchResultsAndFavoritesEnum.REQUEST_ITEMS,
    SearchResultsAndFavoritesEnum.BUY_ITEMS,
    SearchResultsAndFavoritesEnum.MEMO_ITEMS,
    SearchResultsAndFavoritesEnum.HOLD_ITEMS,
  ];
  const actionFunction = (actionName: SearchResultsAndFavoritesEnum) => {
    if (itemCount > 0) {
      if (
        actionName === SearchResultsAndFavoritesEnum.SHARE_EMAIL_EXCEL_PDF ||
        actionName === SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF
      ) {
        if (actionName === SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF) {
          if (
            exportEnabled &&
            (latestExportData === null ||
              (latestExportData?.status !== ExportStatus.in_progress &&
                latestExportData?.status !== ExportStatus.pending &&
                latestExportData?.status !== ExportStatus.hidden))
          ) {
            selectOption(actionName);
          }
        } else {
          selectOption(actionName);
        }
      } else if (
        (actionName === SearchResultsAndFavoritesEnum.SHARE_EMAIL ||
          actionName === SearchResultsAndFavoritesEnum.SHARE_NATIVE ||
          actionName === SearchResultsAndFavoritesEnum.SHARE_VIA_LINK) &&
        itemCount <= 50
      ) {
        selectOption(actionName);
      } else if (
        actionName === SearchResultsAndFavoritesEnum.BUY_NOW ||
        actionName === SearchResultsAndFavoritesEnum.SEND_QUOTE ||
        actionName === SearchResultsAndFavoritesEnum.ADD_TO_CART
      ) {
        if (
          !hasBulkOrderLimit ||
          itemCount <= (bulkOrderLimitCount as number)
        ) {
          selectOption(actionName);
        }
      } else if (
        actionName !== SearchResultsAndFavoritesEnum.SHARE_EMAIL &&
        actionName !== SearchResultsAndFavoritesEnum.SHARE_NATIVE &&
        actionName !== SearchResultsAndFavoritesEnum.SHARE_VIA_LINK &&
        actionName !== SearchResultsAndFavoritesEnum.BUY_NOW &&
        actionName !== SearchResultsAndFavoritesEnum.SEND_QUOTE &&
        actionName !== SearchResultsAndFavoritesEnum.ADD_TO_CART
      ) {
        selectOption(actionName);
      }
    } else if (itemCount === 0) {
      if (
        actionName === SearchResultsAndFavoritesEnum.SHARE_EMAIL_EXCEL_PDF &&
        (totalItems ?? 0) > 0
      ) {
        selectOption(actionName);
      } else if (
        actionName === SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF &&
        exportEnabled &&
        (latestExportData === null ||
          (latestExportData?.status !== ExportStatus.in_progress &&
            latestExportData?.status !== ExportStatus.pending &&
            latestExportData?.status !== ExportStatus.hidden)) &&
        (totalItems ?? 0) > 0
      ) {
        selectOption(actionName);
      }
    }
  };

  const safeKey = (value: unknown) =>
    String(value ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = "bulkActionsContent";

  return (
    <ScrollView
      keyboardShouldPersistTaps="always"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      {menuOptions &&
        menuOptions.length > 0 &&
        menuOptions.map((option, index) => {
          const isRequestNowDisable =
            itemCount > bulkItemCount &&
            validOptions.includes(
              option?.name as SearchResultsAndFavoritesEnum
            );
          const isBuyCartDisabled =
            hasBulkOrderLimit &&
            itemCount > (bulkOrderLimitCount as number) &&
            (option.name === SearchResultsAndFavoritesEnum.BUY_NOW ||
              option.name === SearchResultsAndFavoritesEnum.SEND_QUOTE ||
              option.name === SearchResultsAndFavoritesEnum.ADD_TO_CART);

          const getTextColor = () => {
            if (
              option.name ===
              SearchResultsAndFavoritesEnum.SHARE_EMAIL_EXCEL_PDF
            ) {
              return itemCount > 0 ||
                ((totalItems ?? 0) > 0 && itemCount < 1000)
                ? theme["core-05"]
                : theme["core-04"];
            }
            if (
              option.name === SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF &&
              exportEnabled &&
              (latestExportData === null ||
                (latestExportData?.status !== ExportStatus.in_progress &&
                  latestExportData?.status !== ExportStatus.pending &&
                  latestExportData?.status !== ExportStatus.hidden))
            ) {
              return itemCount > 0 || (totalItems ?? 0) > 0
                ? theme["core-05"]
                : theme["core-04"];
            }
            if (itemCount === 0) return theme["core-04"];
            if (isRequestNowDisable) return theme["core-04"];
            if (isBuyCartDisabled) return theme["core-04"];
            if (
              itemCount > 50 &&
              (option.name === SearchResultsAndFavoritesEnum.SHARE_EMAIL ||
                option.name === SearchResultsAndFavoritesEnum.SHARE_NATIVE ||
                option.name === SearchResultsAndFavoritesEnum.SHARE_VIA_LINK)
            ) {
              return theme["core-04"];
            }
            if (
              option.name ===
              SearchResultsAndFavoritesEnum.REMOVE_FROM_COLLECTION
            ) {
              return theme["error-01"];
            }
            if (
              option.name === SearchResultsAndFavoritesEnum.DOWNLOAD_CSV_PDF &&
              exportEnabled &&
              (latestExportData === null ||
                latestExportData?.status === ExportStatus.in_progress ||
                latestExportData?.status === ExportStatus.pending ||
                latestExportData?.status === ExportStatus.hidden)
            ) {
              return theme["core-04"];
            }
            return theme["core-05"];
          };

          const optionKey = safeKey(option?.name || option?.label || index);
          const optionTestId = `${baseTestId}.option.${optionKey}`;
          return (
            <Pressable
              key={index}
              disabled={isRequestNowDisable || isBuyCartDisabled}
              onPress={() => {
                actionFunction(option?.name as SearchResultsAndFavoritesEnum);
              }}
              testID={optionTestId}
              data-testId={optionTestId}
            >
              <Container noPadding={noPadding}>
                <ParentContainer theme={theme}>
                  <OverLay>
                    <OptionContainer>
                      <TextContainer screenWidth={screenWidth}>
                        <VDBText
                          color={getTextColor()}
                          type={typography["H3-M"]}
                        >
                          {t(option.label)}
                        </VDBText>
                      </TextContainer>
                      {!hideArrowIcon && (
                        <VDBIcon
                          name={VDBIcons.ARROW_RIGHT}
                          size={24}
                          color={theme["core-04"]}
                          variant={IconsVariant.VDBCustom}
                        />
                      )}
                    </OptionContainer>

                    {isRequestNowDisable && (
                      <VDBText
                        type={typography["Minor-R"]}
                        color={theme["core-06"]}
                      >
                        {t(option.description + "", {
                          selection: bulkItemCount,
                        })}
                      </VDBText>
                    )}
                    {itemCount > 50 &&
                      (option.name ===
                        SearchResultsAndFavoritesEnum.SHARE_EMAIL ||
                        option.name ===
                          SearchResultsAndFavoritesEnum.SHARE_NATIVE ||
                        option.name ===
                          SearchResultsAndFavoritesEnum.SHARE_VIA_LINK) && (
                        <VDBText
                          type={typography["Minor-R"]}
                          color={theme["core-06"]}
                        >
                          {t("searchResult.bulkActions.share.screenText.text")}
                        </VDBText>
                      )}
                    {isBuyCartDisabled && (
                      <VDBText
                        type={typography["Minor-R"]}
                        color={theme["core-06"]}
                      >
                        {option.name ===
                          SearchResultsAndFavoritesEnum.BUY_NOW ||
                        option.name === SearchResultsAndFavoritesEnum.SEND_QUOTE
                          ? t(
                              "searchResult.bulkActions.buyNow.screenText.text",
                              {
                                selection: bulkOrderLimitCount,
                                defaultValue: `You can't add to cart or order more than ${bulkOrderLimitCount} items at once`,
                              }
                            )
                          : t(
                              "searchResult.bulkActions.addToCart.screenText.text",
                              {
                                selection: bulkOrderLimitCount,
                                defaultValue: `You can't add to cart or order more than ${bulkOrderLimitCount} items at once`,
                              }
                            )}
                      </VDBText>
                    )}
                  </OverLay>
                </ParentContainer>
              </Container>
            </Pressable>
          );
        })}
    </ScrollView>
  );
};

const OverLay = styled.View<{ noPadding?: boolean }>`
  padding: ${({ noPadding }) => (noPadding ? 0 : 16)}px;
  flex-direction: column;
`;

const OptionContainer = styled.View`
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
`;

const ParentContainer = styled.View<any>`
  width: auto;
  flex-direction: column;
  border-bottom-width: 1px;
  border-color: ${({ theme }) => theme["core-03"]};
`;

const TextContainer = styled.View<{ screenWidth?: number }>`
  flex-direction: row;
  flex-wrap: wrap;
  max-width: ${({ screenWidth }) => screenWidth - 100}px;
`;

export default BulkActionsContent;
